/* =============================================================================
   CORE / OPTION-GROUPS — Pure-Logik der Optionssätze-Verwaltung (S188-2)
   =============================================================================
   SSOT: eine Options-/Extra-Gruppe = `menu_categories`-Zeile kind option/extra
   mit role · config · group_key (phase_143). Die MITGLIEDSCHAFT der Wahl-Items
   bleibt compat-first item-seitig (S188-4 retired die Duplikate):
     - option-Item HOME  : meta.optionGroup === group_key
     - option-Item REUSE : meta.optionGroups[] enthält group_key (S171 P5)
     - extra-Item  HOME  : meta.addonGroup === group_key
   Diese Helfer leiten die Item-Meta aus der Gruppe ab (SYNC-VERTRAG) und
   erhalten optionGroups[] IMMER — der Gast-/Konfigurator-Resolver
   (optionsByGroupFromMenu / addonsByGroupFromMenu / optionGroupsFromMenu) darf
   sich NICHT ändern. Keine DOM-/Storage-Abhängigkeit → voll testbar.
============================================================================ */

export const OPTION_ROLES = ['bun', 'patty', 'extras', 'dressing', 'generic'];

export const ROLE_LABELS = {
  bun: 'Brötchen', patty: 'Patty', extras: 'Extras', dressing: 'Dressing', generic: 'Allgemein'
};

/** Anzeige-Label einer Rolle (Fallback „Allgemein"). */
export function roleLabel(role) {
  return ROLE_LABELS[role] || ROLE_LABELS.generic;
}

/** config jsonb → normalisiertes Regel-Objekt {required,multi,min,max}. */
export function normalizeConfig(config) {
  const c = (config && typeof config === 'object') ? config : {};
  const num = v => (v === '' || v == null || !isFinite(Number(v))) ? null : Number(v);
  return { required: !!c.required, multi: !!c.multi, min: num(c.min), max: num(c.max) };
}

/** Regel-Objekt → kompaktes jsonb (nur gesetzte min/max). */
export function configToJson(rules) {
  const r = normalizeConfig(rules);
  const out = { required: r.required, multi: r.multi };
  if (r.min != null) out.min = r.min;
  if (r.max != null) out.max = r.max;
  return out;
}

/** Stabiler group_key für eine NEUE Gruppe (slug), eindeutig gegen existingKeys. */
export function slugifyGroupKey(name, existingKeys) {
  // ä/ö/ü/ß etc. sind nicht [a-z0-9] → werden vom Replace ohnehin zu '_' → kein
  // NFD-Schritt nötig. group_key ist ein interner Slug (Join-Key), keine Anzeige.
  const base = String(name || '').toLowerCase()
    .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'gruppe';
  const taken = new Set(existingKeys || []);
  if (!taken.has(base)) return base;
  let i = 2;
  while (taken.has(base + '_' + i)) i++;
  return base + '_' + i;
}

/**
 * Leaf-Options-/Extra-Gruppen (parentId != null) aus der flachen Kategorie-Liste.
 * Roots (parentId null = Container „Optionen"/„Extras") werden ausgelassen.
 * Sortiert nach kind (option < extra) → sortOrder → name.
 * @returns {Array<{id,name,kind,parentId,role,config,groupKey,sortOrder,active}>}
 */
export function listOptionGroups(categories) {
  const rank = { option: 0, extra: 1 };
  return (Array.isArray(categories) ? categories : [])
    .filter(c => (c.kind === 'option' || c.kind === 'extra') && c.parentId)
    .map(c => ({
      id: c.id, name: c.name, kind: c.kind, parentId: c.parentId,
      role: c.role || 'generic', config: normalizeConfig(c.config),
      groupKey: c.groupKey || null, sortOrder: Number(c.sortOrder) || 0, active: c.active !== false
    }))
    .sort((a, b) => (rank[a.kind] - rank[b.kind])
      || (a.sortOrder - b.sortOrder)
      || String(a.name).localeCompare(String(b.name)));
}

/** Root-Kategorie eines kinds (parentId null) — Heimat neuer Gruppen. */
export function rootForKind(categories, kind) {
  return (Array.isArray(categories) ? categories : [])
    .find(c => c.kind === kind && !c.parentId) || null;
}

/** Membership eines Items an einer Gruppe — Spiegel des Gast-Resolvers.
 *  @returns {'home'|'reuse'|null} */
export function itemGroupMembership(item, group) {
  if (!item || !group || !group.groupKey) return null;
  const meta = item.meta || {};
  if (group.kind === 'option') {
    if (item.itemType !== 'option') return null;
    if (meta.optionGroup === group.groupKey) return 'home';
    if (Array.isArray(meta.optionGroups) && meta.optionGroups.includes(group.groupKey)) return 'reuse';
    return null;
  }
  if (item.itemType !== 'extra') return null;
  return meta.addonGroup === group.groupKey ? 'home' : null;
}

/** Wahl-Items einer Gruppe: [{item, membership}] nach displayOrder. */
export function itemsForGroup(menu, group) {
  return (Array.isArray(menu) ? menu : [])
    .filter(m => m && !m.deletedAt)
    .map(m => ({ item: m, membership: itemGroupMembership(m, group) }))
    .filter(x => x.membership)
    .sort((a, b) => (Number(a.item.displayOrder) || 0) - (Number(b.item.displayOrder) || 0));
}

/** SYNC-VERTRAG: Meta so ableiten, dass das Item der Gruppe als HOME angehört.
 *  optionGroup (option) / addonGroup (extra) = group_key; optionGroups[] und
 *  ALLE anderen Meta-Keys bleiben unverändert erhalten. */
export function buildGroupItemMeta(group, baseMeta) {
  const meta = { ...((baseMeta && typeof baseMeta === 'object') ? baseMeta : {}) };
  if (group.kind === 'option') meta.optionGroup = group.groupKey;
  else meta.addonGroup = group.groupKey;
  return meta;
}

/** Reuse ergänzen: group_key in optionGroups[] (dedup); optionGroup (Home) unberührt. */
export function addReuseToMeta(baseMeta, groupKey) {
  const meta = { ...((baseMeta && typeof baseMeta === 'object') ? baseMeta : {}) };
  const arr = Array.isArray(meta.optionGroups) ? meta.optionGroups.slice() : [];
  if (groupKey && !arr.includes(groupKey)) arr.push(groupKey);
  meta.optionGroups = arr;
  return meta;
}

/** Reuse entfernen: group_key aus optionGroups[]; optionGroup (Home) NIE anfassen. */
export function removeReuseFromMeta(baseMeta, groupKey) {
  const meta = { ...((baseMeta && typeof baseMeta === 'object') ? baseMeta : {}) };
  const arr = Array.isArray(meta.optionGroups) ? meta.optionGroups.filter(g => g !== groupKey) : [];
  if (arr.length) meta.optionGroups = arr; else delete meta.optionGroups;
  return meta;
}

/** Storage-Payload für ein Wahl-Item (create/update) — voller Sync-Vertrag.
 *  itemType/categoryId/subcategoryId/subcategory folgen der Gruppe; der
 *  BEFORE-Trigger menu_items_sync_category_strings spiegelt die Strings zusätzlich. */
export function groupItemPayload(group, draft) {
  const d = draft || {};
  const price = Number(d.price);
  const payload = {
    name: (d.name || '').trim(),
    itemType: group.kind,            // 'option' | 'extra'
    categoryId: group.parentId,      // Root der Gruppe
    subcategoryId: group.id,         // das Leaf = die Gruppe
    subcategory: group.name,         // Label
    price: isFinite(price) ? price : 0,
    available: d.available !== false,
    meta: buildGroupItemMeta(group, d.meta),
    displayOrder: Number(d.displayOrder) || 0
  };
  if (d.id) payload.id = d.id;
  return payload;
}

/** Validierung eines Gruppen-Drafts (Name Pflicht, Rolle gültig). */
export function validateGroupDraft(draft) {
  const d = draft || {};
  const errors = [];
  if (!(d.name || '').trim()) errors.push('Name fehlt');
  if (d.role && !OPTION_ROLES.includes(d.role)) errors.push('Rolle ungültig');
  return { valid: errors.length === 0, errors };
}

// S206 — Inline-Create: pure Helfer für den Draft→Payload-Bau der neuen
// Fließband-/Panel-Anlage (leere Zeilen ignorieren, kind-Defaults).

/** Draft-Zeile „befüllt"? = getrimmter Name vorhanden (Leere-Zeilen-Filter). */
export function draftRowFilled(row) {
  return !!(row && String(row.name || '').trim());
}

/** Nur befüllte Draft-Zeilen (leere Zeilen werden ignoriert). */
export function filterFilledRows(rows) {
  return (Array.isArray(rows) ? rows : []).filter(draftRowFilled);
}

/** kind-Defaults (Rolle/config) wie im Editor: extra = extras/optional-multi,
 *  option = generic/pflicht-single. Regeln sind dish-driven (S193) — config ist
 *  nur der historische Default, den der Gericht-Konfigurator je Satz überschreibt. */
export function groupKindDefaults(kind) {
  return kind === 'extra'
    ? { role: 'extras', config: { required: false, multi: true } }
    : { role: 'generic', config: { required: true, multi: false } };
}
